import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  Paper,
  Avatar,
  Chip,
  alpha,
  useTheme,
  Stack,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/Header";
import ShopIcon from '@mui/icons-material/Shop';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Button } from "@mui/material";
import Swal from "sweetalert2";
import {
  fetchConnectSummary,
  openConnectPortal,
  openStripeUrl,
  resendConnectOnboardingLink,
  startConnectOnboarding,
} from "../../api/stripeConnect";
import { tokens } from "../../theme";
import { API_BASE_URL } from "../../config/apiBase";

const SellerGetById = () => {
  const { id } = useParams();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectSummary, setConnectSummary] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const theme = useTheme();
  
  const baseColors = tokens(theme.palette.mode);

  const colors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072130",
    },
  };

  useEffect(() => {
    const fetchSeller = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/seller/getSeller/${id}`
        );
        if (response.data.success) {
          setSeller(response.data.seller);
        } else {
          throw new Error("Failed to fetch seller data");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSeller();
  }, [id]);

  const handleConnectAction = async (action) => {
    if (!seller) return;
    setConnectLoading(true);
    try {
      let result;
      if (action === "onboarding") {
        result = await startConnectOnboarding(seller.id);
        if (result.accountId) {
          setSeller((prev) => ({ ...prev, stripeaccountID: result.accountId }));
        }
      } else if (action === "portal") {
        result = await openConnectPortal(seller.id);
      } else if (action === "resend") {
        if (!seller.stripeaccountID) {
          throw new Error("Seller has no Stripe Connect account yet.");
        }
        result = await resendConnectOnboardingLink(
          seller.stripeaccountID,
          seller.id
        );
      } else if (action === "summary") {
        result = await fetchConnectSummary(seller.id);
        setConnectSummary(result);
        return;
      }

      const url = result?.url || result?.onboardingUrl;
      if (url) {
        openStripeUrl(url);
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Connect action failed.", "error");
    } finally {
      setConnectLoading(false);
    }
  };

  const formatField = (key, value) => {
    if (value === null || value === undefined) return "N/A";

    // Handle boolean values
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    // Handle dates
    if (key === "createdAt" || key === "updatedAt" || key === "birthday") {
      return new Date(value).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    return value;
  };

  const formatLabel = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .split(/(?=[A-Z])/)
      .join(" ")
      .replace(/^./, (str) => str.toUpperCase());
  };

  const renderValue = (key, value) => {
    // Handle images and documents
    if (
      key.includes("imageURL") ||
      key.includes("Licence") ||
      key.includes("Id")
    ) {
      return value ? (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Paper elevation={3} sx={{ 
            padding: 1, 
            display: 'inline-block', 
            background: '#ffffff',
            border: `1px solid #e0e0e0`,
            borderRadius: 2,
          }}>
            <img
              src={value}
              alt={formatLabel(key)}
              style={{
                maxWidth: "100%",
                maxHeight: "250px",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              }}
            />
          </Paper>
        </Box>
      ) : (
        <Chip 
          label="No Image Available" 
          variant="outlined" 
          color="default"
          sx={{ mt: 1 }}
        />
      );
    }

    // Handle boolean status fields
    if (key.startsWith("is")) {
      return (
        <Chip
          icon={value ? <CheckCircleIcon /> : <CancelIcon />}
          label={value ? "Yes" : "No"}
          color={value ? "success" : "error"}
          variant="filled"
          sx={{ fontWeight: "medium", mt: 1 }}
        />
      );
    }

    // Handle date fields with icon
    if (key === "createdAt" || key === "updatedAt" || key === "birthday") {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <CalendarTodayIcon sx={{ mr: 1, color: colors.blueAccent[500], fontSize: 20 }} />
          <Typography variant="body1">{formatField(key, value)}</Typography>
        </Box>
      );
    }

    return <Typography variant="body1" sx={{ mt: 1 }}>{formatField(key, value)}</Typography>;
  };

  // Filter out unwanted fields including FCM token
  const getFilteredSellerEntries = () => {
    if (!seller) return [];
    
    const excludedFields = [
      'notificationToken', 
      'otp', 
      'otpExpiryTime', 
      'password',
      'fcmToken'  // Added FCM token to excluded fields
    ];
    return Object.entries(seller).filter(([key]) => !excludedFields.includes(key));
  };

  const filteredSellerEntries = getFilteredSellerEntries();

  return (
    <Box m="20px" height="80vh" overflow="auto" paddingRight="20px">
      {/* Header Section - Matching Dashboard Style */}
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[400]} 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <PersonOutlineIcon sx={{ color: "white", fontSize: 28 }} />
          <Typography variant="h3" fontWeight="bold" color="white">
            Seller Details #{id}
          </Typography>
        </Box>
        <Typography variant="body1" color="white">
          View detailed information about the seller
        </Typography>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress color="primary" thickness={4} />
        </Box>
      ) : error ? (
        <Paper elevation={2} sx={{ 
          p: 3, 
          mt: 2, 
          textAlign: 'center', 
          borderLeft: '4px solid #f44336',
          borderRadius: 2,
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
        }}>
          <Typography color="error" align="center" variant="h6">
            {error}
          </Typography>
        </Paper>
      ) : (
        <Card sx={{ 
          mt: 4, 
          mb: 4, 
          borderRadius: '12px',
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
          overflow: 'hidden',
          backgroundColor: "#ffffff",
        }}>
          {seller?.imageURL && (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: 3,
              background: `linear-gradient(90deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[400]} 100%)`,
              color: 'white'
            }}>
              <Avatar 
                src={seller.imageURL} 
                alt={seller.name || "Seller"}
                sx={{ 
                  width: 100, 
                  height: 100, 
                  border: '4px solid white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
              <Typography variant="h5" sx={{ mt: 2, fontWeight: 'bold' }}>
                {seller.name || seller.shopName || `Seller #${id}`}
              </Typography>
              {seller.shopName && seller.name && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <ShopIcon sx={{ mr: 1, fontSize: 18 }} />
                  <Typography variant="subtitle1">{seller.shopName}</Typography>
                </Box>
              )}
            </Box>
          )}
          <CardContent sx={{ padding: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                borderColor: colors.blueAccent[500],
                backgroundColor: alpha(colors.blueAccent[500], 0.05),
              }}
            >
              <Typography variant="h6" gutterBottom>
                Stripe Connect payouts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Register or manage this seller&apos;s Stripe Express account for
                trip payouts.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  disabled={connectLoading || !seller.email}
                  onClick={() => handleConnectAction("onboarding")}
                  sx={{ backgroundColor: colors.blueAccent[500] }}
                >
                  Start onboarding
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AccountBalanceIcon />}
                  disabled={connectLoading}
                  onClick={() => handleConnectAction("portal")}
                >
                  Open portal
                </Button>
                {seller.stripeaccountID && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SendIcon />}
                      disabled={connectLoading}
                      onClick={() => handleConnectAction("resend")}
                    >
                      Resend link
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                      disabled={connectLoading}
                      onClick={() => handleConnectAction("summary")}
                    >
                      Account summary
                    </Button>
                  </>
                )}
              </Stack>
              {connectSummary && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Payouts enabled:{" "}
                    {connectSummary.payoutsEnabled ? "Yes" : "No"} · Details
                    submitted:{" "}
                    {connectSummary.detailsSubmitted ? "Yes" : "No"}
                  </Typography>
                </Box>
              )}
            </Paper>
            <Grid container spacing={3}>
              {filteredSellerEntries.map(([key, value], index) => (
                <React.Fragment key={key}>
                  <Grid item xs={12} sm={3}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ 
                        borderLeft: `3px solid ${colors.blueAccent[500]}`,
                        pl: 1.5,
                        lineHeight: 1.5,
                        color: colors.blueAccent[500]
                      }}
                    >
                      {formatLabel(key)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    {renderValue(key, value)}
                  </Grid>
                  {index < filteredSellerEntries.length - 1 && (
                    <Grid item xs={12}>
                      <Divider sx={{ 
                        my: 2,
                        opacity: 0.6,
                        borderColor: '#e0e0e0'
                      }} />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SellerGetById;